import { useCallback } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'

import { GoodieFile } from '@/features/files'
import { formToGraphql, FormTranslations } from '@/features/translations'
import { batchReplaceNestedFieldsWithIds } from '@/utils/batchReplaceNestedFields'
import { ButtonRow, isButtonRow, isGraphqlButtonRow } from '../types'
import unset from 'lodash.unset'

export type FormData = {
  header?: string | null
  translations: FormTranslations
  items: {
    type: GraphQL.ButtonRowItemType
    icon: GraphQL.ButtonRowItemIcon
    label: string
    link?: string | null
    integration_door_id?: number | null
    file?: GoodieFile | null
    translations: FormTranslations
  }[]
}

export const TRANSLATABLE_FIELDS: (keyof FormData)[] = ['header']

export const BUTTON_TRANSLATABLE_FIELDS: (keyof FormData['items'][0])[] = [
  'label'
]

export const ALL_TRANSLATABLE_FIELDS = ['header', 'items.[].label']

export function useUploadButtonRow() {
  const [upsert, { loading }] = useMutation(AdminAPI.buttonRow.UPSERT)

  const upload = useCallback(
    (data: ButtonRow) => {
      if (isGraphqlButtonRow(data)) return Promise.resolve(data)

      const finalData = data
      unset(finalData, 'isDirty')
      unset(finalData, 'localeHeader')

      const input = {
        ...finalData,
        translations: finalData.translations
          ? formToGraphql(finalData.translations)
          : [],
        items: finalData.items.map(function (item, index) {
          return {
            ...item,
            localeLabel: undefined,
            translations: item.translations
              ? formToGraphql(item.translations, {
                  prefixToRemove: `items.${index}.`
                })
              : []
          }
        })
      }

      return upsert({ variables: { input } }).then(({ data, error }) => {
        console.log(error, data)

        if (error) return Promise.reject(error)
        if (!data) return Promise.reject('unknown')

        return Promise.resolve(
          getFragment(
            SharedAPI.buttonRow.BUTTON_ROW_FRAGMENT,
            data.upsertButtonRow
          )
        )
      })
    },
    [upsert]
  )

  return [upload, { loading }] as const
}

export function useUploadNestedButtonRows<TResult extends object>() {
  const [uploadRow, { loading }] = useUploadButtonRow()

  const update = useCallback(
    async <TInput extends object>(object: TInput): Promise<TResult> => {
      return batchReplaceNestedFieldsWithIds<ButtonRow, TInput, TResult>({
        object,
        shouldUpdate: (_, value) => isButtonRow(value),
        batchUpdate: values => {
          return Promise.all(values.map(value => uploadRow(value))).then(rows =>
            rows.map(row => row.id)
          )
        }
      })
    },
    [uploadRow]
  )

  return [update, { loading }] as const
}
